import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react';
import type { ReactNode } from 'react';

import { type ErrorType } from '../data-layer/types';

// Define the shape of the context state
interface IspmErrorState {
  errorType?: ErrorType['type'];
}

// Define a generic action that automatically infers from ISPMState
interface Action<K extends keyof IspmErrorState> {
  type: K;
  value: IspmErrorState[K];
}

// Initial state
export const initialState: IspmErrorState = {};

// Reducer function that infers the key and value types
const reducer = <K extends keyof IspmErrorState>(
  state: IspmErrorState,
  action: Action<K>
): IspmErrorState => ({ ...state, [action.type]: action.value });

// Create the context with a default value
const IspmErrorContext = createContext<
  | {
      ispmErrorState: IspmErrorState;
      updateIspmErrorState: <K extends keyof IspmErrorState>(
        key: K,
        value: IspmErrorState[K]
      ) => void;
    }
  | undefined
>(undefined);

// Provider component that wraps the children
export function IspmErrorProvider({ children }: { children: ReactNode }) {
  const [ispmErrorState, dispatch] = useReducer(reducer, initialState);

  // Function to dispatch actions based on key and value, with no hardcoding
  const updateIspmErrorState = useCallback(
    <K extends keyof IspmErrorState>(key: K, value: IspmErrorState[K]) => {
      dispatch({ type: key, value }); // No hardcoded strings!
    },
    []
  );

  // Memoize the context value to avoid unnecessary re-renders
  const value = useMemo(
    () => ({
      ispmErrorState,
      updateIspmErrorState,
    }),
    [ispmErrorState, updateIspmErrorState]
  );

  return (
    <IspmErrorContext.Provider value={value}>
      {children}
    </IspmErrorContext.Provider>
  );
}

// Custom hook to use the ISPM context
export const useIspmErrorContext = () => {
  const context = useContext(IspmErrorContext);

  if (!context) {
    throw new Error(
      'useIspmErrorContext must be used within an IspmErrorProvider'
    );
  }

  return context;
};
